import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import {
    Search,
    Eye,
    EyeOff,
    Trash2,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    X,
    AlertTriangle,
    Globe,
    Lock,
    AlertCircle,
    Heart
} from 'lucide-react';

interface Character {
    _id: string;
    name: string;
    role?: string;
    description: string;
    avatar?: string;
    isPublic: boolean;
    isNSFW: boolean;
    isDefault: boolean;
    stats?: {
        views: number;
        likes: number;
        chats: number;
    };
    createdAt: string;
    userId?: {
        _id: string;
        username: string;
    };
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

const AdminCharacters: React.FC = () => {
    const [characters, setCharacters] = useState<Character[]>([]);
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, pages: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [publicFilter, setPublicFilter] = useState('');
    const [nsfwFilter, setNsfwFilter] = useState('');
    const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchCharacters = async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20',
                ...(search && { search }),
                ...(publicFilter && { isPublic: publicFilter }),
                ...(nsfwFilter && { isNSFW: nsfwFilter })
            });

            const response = await fetch(`${API_URL}/admin/characters?${params}`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setCharacters(data.characters);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Error fetching characters:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCharacters();
    }, [search, publicFilter, nsfwFilter]);

    const handleTogglePublic = async (characterId: string, currentValue: boolean) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/characters/${characterId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ isPublic: !currentValue })
            });

            if ((await response.json()).success) {
                fetchCharacters(pagination.page);
            }
        } catch (error) {
            console.error('Error updating character:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleNSFW = async (characterId: string, currentValue: boolean) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/characters/${characterId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ isNSFW: !currentValue })
            });

            if ((await response.json()).success) {
                fetchCharacters(pagination.page);
            }
        } catch (error) {
            console.error('Error updating character:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteCharacter = async (characterId: string) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/characters/${characterId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if ((await response.json()).success) {
                fetchCharacters(pagination.page);
                setShowDeleteModal(false);
                setSelectedCharacter(null);
            }
        } catch (error) {
            console.error('Error deleting character:', error);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                    Gestión de Personajes
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {pagination.total} personajes en total
                </p>
            </div>

            {/* Filters */}
            <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    flex: 1,
                    minWidth: '250px',
                    position: 'relative'
                }}>
                    <Search size={18} style={{
                        position: 'absolute',
                        left: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'rgba(255,255,255,0.4)'
                    }} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 12px 12px 44px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            outline: 'none'
                        }}
                    />
                </div>

                <select
                    value={publicFilter}
                    onChange={(e) => setPublicFilter(e.target.value)}
                    style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">Visibilidad</option>
                    <option value="true">Públicos</option>
                    <option value="false">Privados</option>
                </select>

                <select
                    value={nsfwFilter}
                    onChange={(e) => setNsfwFilter(e.target.value)}
                    style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">Contenido</option>
                    <option value="false">SFW</option>
                    <option value="true">NSFW</option>
                </select>

                <button
                    onClick={() => fetchCharacters(pagination.page)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(78, 205, 196, 0.2)',
                        border: '1px solid rgba(78, 205, 196, 0.3)',
                        borderRadius: '12px',
                        color: '#4ecdc4',
                        cursor: 'pointer'
                    }}
                >
                    <RefreshCw size={18} />
                </button>
            </div>

            {/* Characters Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '20px'
            }}>
                {loading ? (
                    <div style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '40px',
                        color: 'rgba(255,255,255,0.5)'
                    }}>
                        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                ) : characters.length === 0 ? (
                    <div style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '40px',
                        color: 'rgba(255,255,255,0.5)'
                    }}>
                        No se encontraron personajes
                    </div>
                ) : (
                    characters.map((character) => (
                        <div
                            key={character._id}
                            style={{
                                background: 'rgba(255,255,255,0.05)',
                                backdropFilter: 'blur(20px)',
                                borderRadius: '20px',
                                padding: '20px',
                                border: '1px solid rgba(255,255,255,0.1)'
                            }}
                        >
                            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                                <img
                                    src={character.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${character.name}`}
                                    alt={character.name}
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '16px',
                                        objectFit: 'cover'
                                    }}
                                />
                                <div style={{ flex: 1 }}>
                                    <h3 style={{
                                        color: 'white',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        marginBottom: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        {character.name}
                                        {character.isDefault && (
                                            <span style={{
                                                fontSize: '10px',
                                                padding: '2px 6px',
                                                background: 'rgba(78, 205, 196, 0.2)',
                                                borderRadius: '4px',
                                                color: '#4ecdc4'
                                            }}>
                                                DEFAULT
                                            </span>
                                        )}
                                    </h3>
                                    {character.role && (
                                        <p style={{
                                            fontSize: '11px',
                                            color: 'rgba(255,255,255,0.5)',
                                            fontStyle: 'italic',
                                            marginBottom: '4px'
                                        }}>
                                            {character.role}
                                        </p>
                                    )}
                                    <p style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '13px',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>Creado por:</span>
                                        <span style={{ color: '#4ecdc4', fontWeight: '500' }}>
                                            {character.userId?.username || 'Sistema'}
                                        </span>
                                    </p>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 8px',
                                            background: character.isPublic ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255,255,255,0.1)',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            color: character.isPublic ? '#4ecdc4' : 'rgba(255,255,255,0.5)'
                                        }}>
                                            {character.isPublic ? <Globe size={12} /> : <Lock size={12} />}
                                            {character.isPublic ? 'Público' : 'Privado'}
                                        </span>
                                        {character.isNSFW && (
                                            <span style={{
                                                padding: '4px 8px',
                                                background: 'rgba(255, 107, 107, 0.2)',
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                color: '#ff6b6b'
                                            }}>
                                                NSFW
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Stats */}
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                padding: '12px 0',
                                borderTop: '1px solid rgba(255,255,255,0.1)',
                                marginBottom: '12px'
                            }}>
                                <div style={{ textAlign: 'center', flex: 1 }}>
                                    <Eye size={16} style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }} />
                                    <p style={{ color: 'white', fontWeight: '600' }}>{character.stats?.views || 0}</p>
                                </div>
                                <div style={{ textAlign: 'center', flex: 1 }}>
                                    <Heart size={16} style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }} />
                                    <p style={{ color: 'white', fontWeight: '600' }}>{character.stats?.likes || 0}</p>
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => handleTogglePublic(character._id, character.isPublic)}
                                    disabled={actionLoading}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: character.isPublic ? 'rgba(255,255,255,0.1)' : 'rgba(78, 205, 196, 0.2)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: character.isPublic ? 'rgba(255,255,255,0.6)' : '#4ecdc4',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                        fontSize: '13px'
                                    }}
                                >
                                    {character.isPublic ? <EyeOff size={16} /> : <Eye size={16} />}
                                    {character.isPublic ? 'Ocultar' : 'Publicar'}
                                </button>
                                <button
                                    onClick={() => handleToggleNSFW(character._id, character.isNSFW)}
                                    disabled={actionLoading}
                                    style={{
                                        padding: '10px 14px',
                                        background: character.isNSFW ? 'rgba(255, 107, 107, 0.2)' : 'rgba(255,255,255,0.1)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: character.isNSFW ? '#ff6b6b' : 'rgba(255,255,255,0.6)',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600'
                                    }}
                                >
                                    NSFW
                                </button>
                                <button
                                    onClick={() => { setSelectedCharacter(character); setShowDeleteModal(true); }}
                                    style={{
                                        padding: '10px',
                                        background: 'rgba(255, 107, 107, 0.2)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: '#ff6b6b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '24px'
                }}>
                    <button
                        onClick={() => fetchCharacters(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        style={{
                            padding: '10px 16px',
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '10px',
                            color: 'white',
                            cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                            opacity: pagination.page === 1 ? 0.5 : 1
                        }}
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                        Página {pagination.page} de {pagination.pages}
                    </span>
                    <button
                        onClick={() => fetchCharacters(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        style={{
                            padding: '10px 16px',
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '10px',
                            color: 'white',
                            cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer',
                            opacity: pagination.page === pagination.pages ? 0.5 : 1
                        }}
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedCharacter && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '24px',
                        padding: '32px',
                        width: '400px',
                        border: '1px solid rgba(255, 107, 107, 0.3)',
                        textAlign: 'center'
                    }}>
                        <AlertTriangle size={48} style={{ color: '#ff6b6b', marginBottom: '16px' }} />
                        <h2 style={{ color: 'white', fontSize: '20px', marginBottom: '8px' }}>¿Eliminar Personaje?</h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>
                            Esta acción eliminará permanentemente a <strong>{selectedCharacter.name}</strong> y todas sus imágenes.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteCharacter(selectedCharacter._id)}
                                disabled={actionLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {actionLoading ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AdminCharacters;
